/**
 * useLifecycleActions Hook
 *
 * Provides lifecycle action utilities including cache invalidation
 * after successful lifecycle transitions.
 */

import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { LifecycleMetadata } from '@/services/metadataService'

/**
 * Options for useLifecycleActions hook
 */
interface UseLifecycleActionsOptions {
  /** API endpoint (e.g., '/offerings') */
  endpoint: string
  /** React Query key for the table data */
  queryKey?: string[]
  /** Lifecycle metadata (optional - if not provided, lifecycle is disabled) */
  lifecycle?: LifecycleMetadata
  /** Additional callback after successful action */
  onActionSuccess?: () => void
}

/**
 * Result from useLifecycleActions hook
 */
interface UseLifecycleActionsResult {
  /** Whether lifecycle actions are enabled */
  lifecycleEnabled: boolean
  /** Handler to call after successful lifecycle action (invalidates cache) */
  handleActionSuccess: () => void
  /** The state field name from lifecycle metadata */
  stateField: string | null
}

/**
 * Hook for lifecycle action utilities
 *
 * Provides:
 * - Cache invalidation after lifecycle transitions
 * - Lifecycle enabled flag
 * - State field name extraction
 *
 * @example
 * ```tsx
 * const { lifecycleEnabled, handleActionSuccess, stateField } = useLifecycleActions({
 *   endpoint: '/offerings',
 *   queryKey: ['tableData', '/offerings'],
 *   lifecycle: metadata.lifecycle,
 *   onActionSuccess: () => console.log('Action completed'),
 * })
 *
 * // In LifecycleActions component
 * <LifecycleActions
 *   lifecycle={lifecycle}
 *   onSuccess={handleActionSuccess}
 *   ...
 * />
 * ```
 */
export function useLifecycleActions({
  endpoint,
  queryKey,
  lifecycle,
  onActionSuccess,
}: UseLifecycleActionsOptions): UseLifecycleActionsResult {
  const queryClient = useQueryClient()

  // Determine if lifecycle is enabled
  const lifecycleEnabled = Boolean(lifecycle?.enabled && lifecycle?.actions)

  // Get state field from metadata
  const stateField = lifecycle?.stateField ?? null

  // Handler to invalidate cache after successful action
  const handleActionSuccess = useCallback(() => {
    // Invalidate the table data query
    const key = queryKey || ['tableData', endpoint]
    queryClient.invalidateQueries({ queryKey: key })

    // Also invalidate any detail queries for this endpoint
    queryClient.invalidateQueries({
      queryKey: ['record', endpoint],
      exact: false,
    })

    // Call additional success handler if provided
    onActionSuccess?.()
  }, [queryClient, queryKey, endpoint, onActionSuccess])

  return {
    lifecycleEnabled,
    handleActionSuccess,
    stateField,
  }
}
